import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Clock, ArrowLeft } from "lucide-react";
import { postBySlug, POSTS } from "../data/blog";
import NotFound from "./NotFound";
import { useSeo, articleLd, breadcrumbLd } from "../lib/seo";

function Block({ block }) {
  if (block.h2) return <h2 className="mt-10 font-display text-2xl sm:text-3xl">{block.h2}</h2>;
  if (block.p) return <p className="mt-4 leading-relaxed text-muted-foreground">{block.p}</p>;
  if (block.ul)
    return (
      <ul className="mt-4 space-y-2">
        {block.ul.map((li, i) => (
          <li key={i} className="flex items-start gap-2 text-muted-foreground"><span className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-primary" />{li}</li>
        ))}
      </ul>
    );
  if (block.cta)
    return (
      <Link to="/" className="mt-8 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:brightness-110" data-testid="blog-cta">
        {block.cta} <ArrowRight className="h-4 w-4" />
      </Link>
    );
  return null;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = postBySlug(slug);

  useSeo({
    title: post?.title,
    description: post?.excerpt,
    path: `/blog/${slug}`,
    type: "article",
    jsonLd: post
      ? [articleLd({ title: post.title, description: post.excerpt, slug: post.slug, date: post.date, author: post.author }),
         breadcrumbLd([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${post.slug}` }])]
      : null,
  });

  if (!post) return <NotFound />;
  const related = (post.related || []).map(postBySlug).filter(Boolean);

  return (
    <article className="container max-w-3xl py-12">
      <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground transition hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All posts</Link>

      <span className="mt-6 block text-xs font-semibold text-primary">{post.category}</span>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl">{post.title}</h1>

      <div className="mt-5 flex items-center gap-4 border-b border-border pb-6 text-sm text-muted-foreground">
        <span>By {post.author}</span>
        <span>{new Date(post.date).toLocaleDateString()}</span>
        <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {post.read} min read</span>
      </div>

      <div className="mt-4 rounded-lg border border-border bg-card p-5">
        <p className="label-eyebrow mb-2">TL;DR</p>
        <p className="text-sm text-muted-foreground">{post.excerpt}</p>
      </div>

      <div className="mt-2">
        {post.body.map((b, i) => <Block key={i} block={b} />)}
      </div>

      <div className="mt-12 rounded-xl border border-primary/30 bg-card p-6 text-center">
        <h3 className="font-display text-2xl">See your own scores in seconds</h3>
        <p className="mt-2 text-sm text-muted-foreground">Free, instant, no app download.</p>
        <Link to="/" className="mt-4 inline-block rounded-md bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground">Scan my face free</Link>
      </div>

      {related.length > 0 && (
        <div className="mt-12">
          <h3 className="font-display text-xl">Keep reading</h3>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {related.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="rounded-xl border border-border bg-card p-5 transition hover:border-primary/40">
                <span className="text-xs font-semibold text-primary">{r.category}</span>
                <p className="mt-1 font-display text-lg">{r.title}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
